import { AlertStatus, type Alert } from '@/types/nest/alert'
import { axiosClient } from '@/utils/axios-client'
import type { UUID } from 'crypto'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useAlertStore = defineStore('alertStore', () => {
  /*
   * Fetch: {@link fetchAlerts}
   */
  const alerts = ref<Alert[]>([])

  /*
   * Fetch: {@link fetchRecentAlerts}
   */
  const recentAlerts = ref<Alert[]>([])

  /*
   * Fetch: {@link fetchUnreadAlertCount}
   */
  const unreadAlertCount = ref<number>(0)

  /*
   * Data: {@link alerts}
   */
  async function fetchAlerts(): Promise<void> {
    const response = await axiosClient.request<Alert[]>({
      url: '/alerts',
      method: 'GET',
    })

    alerts.value = response.data
  }

  /*
   * Data: {@link recentAlerts}
   */
  async function fetchRecentAlerts(from: Date | null): Promise<void> {
    const response = await axiosClient.request<Alert[]>({
      url: '/alerts',
      method: 'GET',
      params: { from: from?.toISOString() },
    })

    recentAlerts.value = response.data.filter(alert => alert.status !== AlertStatus.ARCHIVED)
  }

  /*
   * Data: {@link unreadAlertCount}
   */
  async function fetchUnreadAlertCount(): Promise<void> {
    const response = await axiosClient.request<Alert[]>({
      url: '/alerts',
      method: 'GET',
      params: { status: AlertStatus.UNREAD },
    })

    unreadAlertCount.value = response.data.length
  }

  async function updateAlertStatus(id: UUID, status: AlertStatus): Promise<void> {
    await axiosClient.request({
      url: `/alerts/${id}/status`,
      method: 'PATCH',
      data: { status },
    })

    for (const alert of [...alerts.value, ...recentAlerts.value]) {
      if (alert.id === id) alert.status = status
    }
    recentAlerts.value = recentAlerts.value.filter(alert => alert.status !== AlertStatus.ARCHIVED)

    await fetchUnreadAlertCount()
  }

  async function markAllAlertsAsRead(): Promise<void> {
    await axiosClient.request({
      url: '/alerts/read',
      method: 'PATCH',
    })

    for (const alert of [...alerts.value, ...recentAlerts.value]) {
      if (alert.status === AlertStatus.UNREAD) alert.status = AlertStatus.READ
    }
    unreadAlertCount.value = 0
  }

  return {
    alerts,
    recentAlerts,
    unreadAlertCount,
    fetchAlerts,
    fetchRecentAlerts,
    fetchUnreadAlertCount,
    updateAlertStatus,
    markAllAlertsAsRead,
  }
})
